import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  clearOnboardingSnapshot,
  loadOnboardingSnapshot,
  saveOnboardingSnapshot,
  type OnboardingFlowSnapshot,
} from './storage';
import type { OnboardingSetupForm } from '@/types/onboarding';
import { useSession } from '@/lib/hooks/useSession';

interface OnboardingFlowValue {
  tenantId: string | null;
  tenantSlug: string | null;
  tenantName: string | null;
  email: string | null;
  paymentComplete: boolean;
  questionnaire: OnboardingSetupForm | null;
  hasSignupContext: boolean;
  hydrated: boolean;
  setSignupContext: (details: {
    tenantId: string;
    tenantSlug: string;
    tenantName: string | null;
    email: string;
  }) => void;
  markPaymentComplete: () => void;
  saveQuestionnaire: (answers: OnboardingSetupForm) => void;
  reset: () => void;
}

interface InternalState {
  tenantId: string | null;
  tenantSlug: string | null;
  tenantName: string | null;
  email: string | null;
  paymentComplete: boolean;
  questionnaire: OnboardingSetupForm | null;
}

const defaultState: InternalState = {
  tenantId: null,
  tenantSlug: null,
  tenantName: null,
  email: null,
  paymentComplete: false,
  questionnaire: null,
};

const OnboardingFlowContext = createContext<OnboardingFlowValue | null>(null);

const snapshotToState = (snapshot: OnboardingFlowSnapshot | null): InternalState => {
  if (!snapshot) {
    return defaultState;
  }
  return {
    tenantId: snapshot.tenantId,
    tenantSlug: snapshot.tenantSlug,
    tenantName: snapshot.tenantName ?? null,
    email: snapshot.email,
    paymentComplete: snapshot.paymentComplete ?? false,
    questionnaire: snapshot.questionnaire ?? null,
  };
};

export const OnboardingFlowProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<InternalState>(() => snapshotToState(loadOnboardingSnapshot()));
  const [hydrated, setHydrated] = useState(false);
  const session = useSession();

  const setSignupContext = useCallback<OnboardingFlowValue['setSignupContext']>((details) => {
    setState({
      tenantId: details.tenantId,
      tenantSlug: details.tenantSlug,
      tenantName: details.tenantName ?? null,
      email: details.email,
      paymentComplete: false,
      questionnaire: null,
    });
  }, []);

  const markPaymentComplete = useCallback(() => {
    setState((prev) => {
      if (!prev.tenantId) {
        return prev;
      }
      if (prev.paymentComplete) {
        return prev;
      }
      return {
        ...prev,
        paymentComplete: true,
      };
    });
  }, []);

  const saveQuestionnaire = useCallback((answers: OnboardingSetupForm) => {
    setState((prev) => {
      if (!prev.tenantId) {
        return prev;
      }
      return {
        ...prev,
        questionnaire: answers,
      };
    });
  }, []);

  const reset = useCallback(() => {
    setState(defaultState);
    clearOnboardingSnapshot();
  }, []);

  useEffect(() => {
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) {
      return;
    }

    if (session.loading) {
      return;
    }

    const tenant = session.tenant;
    const userEmail = session.user?.email ?? null;

    if (!tenant || !userEmail) {
      return;
    }

    setState((previous) => {
      if (
        previous.tenantId === tenant.id &&
        previous.tenantSlug === tenant.slug &&
        previous.email === userEmail &&
        previous.tenantName === (tenant.name ?? null)
      ) {
        return previous;
      }

      return {
        tenantId: tenant.id,
        tenantSlug: tenant.slug,
        tenantName: tenant.name ?? null,
        email: userEmail,
        paymentComplete: previous.paymentComplete,
        questionnaire: previous.questionnaire,
      };
    });
  }, [
    hydrated,
    session.loading,
    session.tenant,
    session.user,
  ]);

  useEffect(() => {
    if (!hydrated) {
      return;
    }

    if (!state.tenantId || !state.tenantSlug || !state.email) {
      clearOnboardingSnapshot();
      return;
    }

    saveOnboardingSnapshot({
      tenantId: state.tenantId,
      tenantSlug: state.tenantSlug,
      tenantName: state.tenantName ?? null,
      email: state.email,
      paymentComplete: state.paymentComplete,
      questionnaire: state.questionnaire ?? undefined,
    });
  }, [hydrated, state]);

  const value = useMemo<OnboardingFlowValue>(() => {
    const hasSignupContext = Boolean(state.tenantId && state.tenantSlug && state.email);
    return {
      tenantId: state.tenantId,
      tenantSlug: state.tenantSlug,
      tenantName: state.tenantName,
      email: state.email,
      paymentComplete: state.paymentComplete,
      questionnaire: state.questionnaire,
      hasSignupContext,
      hydrated,
      setSignupContext,
      markPaymentComplete,
      saveQuestionnaire,
      reset,
    };
  }, [hydrated, markPaymentComplete, reset, saveQuestionnaire, setSignupContext, state]);

  return (
    <OnboardingFlowContext.Provider value={value}>
      {children}
    </OnboardingFlowContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useOnboardingFlow = (): OnboardingFlowValue => {
  const context = useContext(OnboardingFlowContext);
  if (!context) {
    throw new Error('useOnboardingFlow must be used within an OnboardingFlowProvider');
  }
  return context;
};
